import { type ReactNode } from "react";
import { Label } from "./primitives";

/**
 * The register.
 *
 * The platform's organising structure, and its own idea. The database is
 * append-only where it matters — publication, approval, submitted versions,
 * audit, file access — and withdrawal is a written fact rather than a deletion.
 * So the primary surface is a record read in order, not a grid of tiles. A tile
 * dashboard would hide the one property the whole system is built on.
 *
 * Three parts, set like a reference page:
 *
 *   rail    a hairline channel carrying the engagement's keys, with a detent
 *           cut wherever the record changes footing
 *   block   short-measure entries, each one act, dense and scannable
 *   margin  wide and mostly bare, where the apparatus hangs
 *
 * Under two columns the margin collapses and its apparatus relocates in-flow
 * above the block it annotates. It is never hidden — an apparatus that
 * disappears on a laptop is an apparatus the Lead learns not to trust.
 */

export function RegisterLayout({
  rail,
  children,
  margin,
}: {
  rail?: ReactNode;
  children: ReactNode;
  margin?: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-8 xl:flex-row xl:gap-0">
      {/* The margin comes first in the DOM on narrow screens, because the
          apparatus is what a Lead opens the screen to read. On wide screens it
          is ordered back to the right. */}
      {margin && (
        <aside className="order-first shrink-0 xl:order-last xl:w-margin xl:border-l xl:border-line xl:pl-7">
          {margin}
        </aside>
      )}

      <div className="order-last flex min-w-0 flex-1 gap-0 xl:order-first xl:pr-8">
        {rail && (
          <div className="hidden shrink-0 border-r border-line pr-4 lg:block lg:w-[4.5rem]">
            {rail}
          </div>
        )}
        <div className="min-w-0 flex-1 lg:pl-5">{children}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- rail ---

export interface RailKey {
  id: string;
  /** One or two characters. The rail is a channel, not a nav bar. */
  key: string;
  label: string;
  state?: "rest" | "live" | "spent";
  onSelect?: () => void;
}

/**
 * The rail's keys and detents.
 *
 * Rebuilt after seeing it rendered: at 3.25rem the keys clipped and the
 * absolutely-positioned detents collided with the rule. The channel has to be
 * wide enough to set a two-character key and a mark beside it without either
 * touching the other.
 *
 * A detent is cut where the record changes footing. At rest it is a hairline
 * square on the rule; live, it fills; spent, it greys. The key sits inboard of
 * it, so the rule stays a clean edge down the page.
 */
export function Rail({ keys }: { keys: RailKey[] }) {
  return (
    <nav aria-label="Sections" className="sticky top-6">
      <ul className="flex flex-col">
        {keys.map((k) => {
          const state = k.state ?? "rest";
          const isLive = state === "live";

          return (
            <li key={k.id} className="relative">
              <button
                type="button"
                onClick={k.onSelect}
                aria-current={isLive ? "true" : undefined}
                className={
                  "group flex w-full items-center justify-end gap-2.5 py-3 pr-3 text-right " +
                  "transition-colors duration-150 ease-damped " +
                  (isLive ? "text-ink" : "text-muted hover:text-ink")
                }
              >
                <span className="font-mono text-2xs uppercase tracking-chip">
                  {k.key}
                </span>
                <span className="sr-only">{k.label}</span>
              </button>

              {/* On the rule, centred on the row, half outside the channel. */}
              <span
                aria-hidden
                className={
                  "rail-detent pointer-events-none absolute -right-4 top-1/2 -translate-y-1/2 translate-x-1/2 " +
                  (isLive
                    ? "rail-detent--live"
                    : state === "spent"
                      ? "rail-detent--spent"
                      : "group-hover:border-ink/50")
                }
              />
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ------------------------------------------------------------------ block ---

/**
 * A region of the block. The rule above the heading is what separates it; there
 * is no card, and `count` sits with the heading rather than in a badge.
 */
export function Region({
  title,
  count,
  action,
  children,
  tone = "rest",
  open,
  onToggle,
  id,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
  tone?: "rest" | "attention";
  /**
   * Pass `open` and `onToggle` to make the region a disclosure.
   *
   * Controlled rather than self-managing because something outside needs to
   * be able to open it — the rail jumps to a region, and jumping to a
   * collapsed heading that then says nothing is worse than not jumping.
   *
   * Omit both and the region behaves exactly as it always did: always open,
   * heading not interactive. Every existing caller is unchanged.
   */
  open?: boolean;
  onToggle?: () => void;
  /** Needed for aria-controls when it is a disclosure. */
  id?: string;
}) {
  const collapsible = typeof open === "boolean" && Boolean(onToggle);
  const panelId = id ? `${id}-panel` : undefined;

  const heading = (
    <div className="flex items-baseline gap-2.5">
      {collapsible && (
        // A caret that turns, rather than a +/- pair. It reads as "there is
        // more below this line" without adding a second kind of control to
        // a screen whose whole argument is restraint.
        <span
          aria-hidden
          className={
            "font-mono text-2xs text-muted transition-transform duration-150 ease-damped " +
            (open ? "rotate-90" : "")
          }
        >
          {"›"}
        </span>
      )}
      <Label as="h2">{title}</Label>
      {count !== undefined && (
        <span data-numeric className="text-2xs text-muted">
          {count}
        </span>
      )}
    </div>
  );

  return (
    <section className="mb-7">
      <header
        className={
          "mb-1 flex items-baseline justify-between gap-4 border-t pt-3 " +
          (tone === "attention" ? "border-ink/30" : "border-line-strong")
        }
      >
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={panelId}
            className="flex items-baseline gap-2.5 text-left text-muted transition-colors duration-150 ease-damped hover:text-ink"
          >
            {heading}
          </button>
        ) : (
          heading
        )}
        {action}
      </header>
      {/* `hidden` rather than unmounting: the record is a list the browser
          can keep, and remounting it on every open would re-run whatever a
          child does on mount. */}
      <div id={panelId} hidden={collapsible && !open}>
        {children}
      </div>
    </section>
  );
}

/**
 * One entry: an act, by someone, at a time.
 *
 * `lead` is the subject, `meta` the attribution, `right` whatever the entry
 * affords. Ruled top; no box. Hovering lifts the ground a fraction rather than
 * moving the row, because a register that shifts under the cursor is hard to
 * read down.
 */
export function Entry({
  lead,
  meta,
  right,
  onClick,
  tone = "rest",
  children,
}: {
  lead: ReactNode;
  meta?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  tone?: "rest" | "attention" | "spent";
  /**
   * A panel that belongs to this row — a decision form, say — shown beneath
   * it. Kept out of `right`, which is a fixed-width strip of marks and
   * buttons, and never rendered inside the interactive variant: a form
   * nested in a button is invalid HTML and swallows its own clicks.
   */
  children?: ReactNode;
}) {
  const interactive = Boolean(onClick);

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div
          className={
            "truncate text-base " +
            (tone === "spent" ? "text-muted line-through" : "text-ink")
          }
        >
          {lead}
        </div>
        {meta && <div className="mt-0.5 truncate text-xs text-muted">{meta}</div>}
      </div>
      {right && <div className="flex shrink-0 items-center gap-3">{right}</div>}
    </>
  );

  const shared =
    "register-entry flex w-full items-center gap-4 py-3 text-left transition-colors duration-150 ease-damped";

  if (!interactive) {
    return (
      <div>
        <div className={shared}>{body}</div>
        {children}
      </div>
    );
  }

  return (
    <div>
      <button type="button" onClick={onClick} className={`${shared} hover:bg-ink/[0.035]`}>
        {body}
      </button>
      {children}
    </div>
  );
}

// ----------------------------------------------------------------- margin ---

/**
 * The apparatus. Hangs in the margin, brackets back to what it annotates.
 *
 * On a narrow viewport this becomes a horizontal strip above the block rather
 * than disappearing.
 */
export function Apparatus({ children }: { children: ReactNode }) {
  return (
    <div className="sticky top-6 flex flex-row flex-wrap gap-x-8 gap-y-6 xl:flex-col xl:gap-7">
      {children}
    </div>
  );
}

/** A note in the margin, set small, bracketed to its subject by a rule. */
export function MarginNote({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-l border-line pl-3 xl:border-l-0 xl:border-t xl:pl-0 xl:pt-3">
      <Label>{title}</Label>
      <div className="mt-1.5 text-xs leading-[1.05rem] text-secondary">{children}</div>
    </div>
  );
}
